import React from "react";
import { motion } from "framer-motion";
import {
    PenTool,
    Palette,
    Share2,
    Monitor,
    Package,
    Lightbulb,
    Square,
    Printer,
    Sparkles,
    ArrowUpRight,
} from "lucide-react";
import { useCollection } from "../lib/useCollection";

const ICONS = { PenTool, Palette, Share2, Monitor, Package, Lightbulb, Square, Printer, Sparkles };

export const SERVICES = [
    {
        id: "logo-design",
        icon: PenTool,
        title: "Logo Design",
        desc: "Distinctive, timeless marks crafted from strategy — not trends.",
    },
    {
        id: "brand-identity",
        icon: Palette,
        title: "Brand Identity",
        desc: "Complete visual systems: typography, color, guidelines, applications.",
    },
    {
        id: "social-media-design",
        icon: Share2,
        title: "Social Media Design",
        desc: "Scroll‑stopping creatives engineered to convert followers into customers.",
    },
    {
        id: "website-design",
        icon: Monitor,
        title: "Website Design",
        desc: "Conversion‑focused websites with premium aesthetics and fast performance.",
    },
    {
        id: "packaging-design",
        icon: Package,
        title: "Packaging Design",
        desc: "Shelf‑ready packaging that tells your story and earns the second look.",
    },
    {
        id: "glow-sign-boards",
        icon: Lightbulb,
        title: "Glow Sign Boards",
        desc: "Bright, durable LED glow signs that make your storefront unmissable.",
    },
    {
        id: "acrylic-sign-boards",
        icon: Square,
        title: "Acrylic Sign Boards",
        desc: "Premium 3D acrylic signage with crisp finishing and long life.",
    },
    {
        id: "shop-signage-printing",
        icon: Printer,
        title: "Shop Signage & Printing",
        desc: "End‑to‑end signage, flex, vinyl & print production at studio quality.",
    },
];

const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.55, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] },
    }),
};

export const ServicesSection = ({ id = "services", showHeading = true, onItemClick }) => {
    const services = useCollection("/services", SERVICES);
    return (
        <section
            id={id}
            data-testid="services-section"
            className="relative py-24 sm:py-32 lg:py-36 bg-[#050505] border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                {showHeading && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl mb-16"
                    >
                        <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-4">
                            What We Create
                        </div>
                        <h2
                            data-testid="services-heading"
                            className="font-poppins font-semibold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl"
                        >
                            Design that does the <span className="gold-gradient-text">heavy lifting</span>.
                        </h2>
                        <p className="mt-5 font-inter text-base leading-relaxed text-zinc-400 max-w-2xl">
                            From the first sketch to the storefront, we handle every visual touchpoint of
                            your brand — with the precision of a studio and the speed of a partner.
                        </p>
                    </motion.div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
                    {services.map((s, i) => {
                        const Icon = s.icon && typeof s.icon !== "string" ? s.icon : ICONS[s.icon] || Sparkles;
                        const desc = s.desc || s.description;
                        return (
                            <motion.button
                                type="button"
                                key={s.id}
                                custom={i}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, amount: 0.2 }}
                                variants={cardVariants}
                                onClick={() => onItemClick && onItemClick(s)}
                                data-testid={`service-card-${s.id}`}
                                className="group relative text-left bg-white/[0.02] border border-white/5 rounded-2xl p-7 hover:border-gold/40 hover:bg-white/[0.04] transition-all duration-300 overflow-hidden"
                            >
                                <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-gold/0 group-hover:bg-gold/10 blur-3xl transition-colors duration-500" />
                                <div className="relative flex items-center justify-between mb-6">
                                    <div className="w-12 h-12 rounded-xl bg-black border border-white/10 flex items-center justify-center group-hover:border-gold/40 transition-colors">
                                        <Icon size={20} className="text-gold" strokeWidth={1.6} />
                                    </div>
                                    <ArrowUpRight
                                        size={18}
                                        className="text-zinc-600 group-hover:text-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                                    />
                                </div>
                                <h3 className="relative font-poppins font-medium text-white text-lg">
                                    {s.title}
                                </h3>
                                <p className="relative mt-2 font-inter text-sm leading-relaxed text-zinc-400">
                                    {desc}
                                </p>
                                <div className="relative mt-6 h-px w-10 bg-gold/40 group-hover:w-full transition-all duration-500" />
                            </motion.button>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default ServicesSection;
